import { Button } from '@praetorian-chariot/ui';
import { AxiosHeaders } from 'axios';
import { toast } from 'sonner';

import { useGetDownloads } from '@/hooks/download';
import { useAxios } from '@/hooks/useAxios';
import { useDownloadFile } from '@/hooks/useFiles';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { GraphQuery, Job, JobStatus } from '@/types';
import { useMutation } from '@/utils/api';

// Constants for polling behavior
const INITIAL_POLL_INTERVAL = 5000; // 5 seconds
const MAX_RETRIES = 60; // 5 minutes total (60 * 5s = 300s)
const MAX_POLL_INTERVAL = 30000; // 30 seconds maximum

interface ExportParams {
  query: GraphQuery;
  columns?: string[];
  items?: string[];
  format?: string;
  email?: string;
  signal?: AbortSignal;
}

export function useAsyncExports(
  type: 'seed' | 'asset' | 'risk' | 'technology' | 'agent',
  hideToast = false
) {
  const axios = useAxios();
  const { mutateAsync: downloadFile } = useDownloadFile();
  const { refetch: refetchDownloads } = useGetDownloads({ enabled: false });

  return useMutation({
    defaultErrorMessage: `Failed to export ${getExportLabels(type)}`,
    mutationFn: async ({
      query,
      columns,
      items,
      format = 'csv',
      email,
      signal,
    }: ExportParams) => {
      const emailHeader = (email
        ? { common: { account: email } }
        : {}) as unknown as AxiosHeaders;

      const generateExport = async (): Promise<Job> => {
        // Check if already aborted
        if (signal?.aborted) {
          throw new DOMException('Export cancelled', 'AbortError');
        }

        // Initial export request with signal
        const exportResponse = await axios.post<Job>(
          `/export/${type}`,
          {
            query: items && items.length > 0 ? undefined : query,
            columns,
            format,
            items,
          },
          {
            headers: emailHeader,
            signal, // ✅ Pass signal to axios
          }
        );

        if (!exportResponse?.data?.key) {
          throw new Error('Invalid export response');
        }

        // Poll for job completion with timeout and exponential backoff
        const checkJobStatus = async (retryCount = 0): Promise<Job> => {
          // Check if aborted
          if (signal?.aborted) {
            throw new DOMException('Export cancelled', 'AbortError');
          }

          // Check timeout
          if (retryCount >= MAX_RETRIES) {
            throw new Error(
              `Export timed out after ${Math.floor((MAX_RETRIES * INITIAL_POLL_INTERVAL) / 60000)} minutes. Please try again with fewer records or additional filters.`
            );
          }

          const jobResponse = await axios.get('/my', {
            headers: emailHeader,
            params: { key: exportResponse.data.key },
            signal, // ✅ Pass signal to axios
          });

          if (!jobResponse?.data?.jobs?.[0]) {
            throw new Error('Invalid job response');
          }
          refetchDownloads();

          const job = jobResponse.data.jobs[0];
          const status = job.status.slice(0, 2);

          if (status === JobStatus.Pass) {
            // Invalidate queries on success
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy('file', `#home/export-${type}`),
            });
            queryClient.invalidateQueries({
              queryKey: getQueryKey.getMy('job', `#home/export-${type}`),
            });
            return job;
          }

          if (status === JobStatus.Fail) {
            const errorMessage = job.message || 'Export job failed';
            throw new Error(errorMessage);
          }

          if (status === JobStatus.Running || status === JobStatus.Queued) {
            // Exponential backoff: 5s, 5s, 10s, 10s, 15s, 15s, 20s, 20s, 25s, 25s, 30s (max)
            // Formula: min(5000 * (1 + floor(retryCount / 2)), MAX_POLL_INTERVAL)
            const interval = Math.min(
              INITIAL_POLL_INTERVAL * (1 + Math.floor(retryCount / 2)),
              MAX_POLL_INTERVAL
            );

            await new Promise(resolve => setTimeout(resolve, interval));
            return checkJobStatus(retryCount + 1);
          }

          throw new Error(`Unknown job status: ${status}`);
        };

        await checkJobStatus();
        return exportResponse.data;
      };

      const promise = generateExport();

      if (!hideToast) {
        // Show toast and wait for export completion
        toast.promise(promise, {
          loading: `Exporting ${getExportLabels(type)}`,
          duration: Infinity,
          error: `Failed to export ${getExportLabels(type)}`,
          closeButton: true,
          success: function SuccessToast(data) {
            return (
              <div className="flex w-full items-center gap-2">
                <p>Exported {getExportLabels(type)}</p>
                <Button
                  label={
                    format === 'csv'
                      ? 'Download CSV File'
                      : 'Download JSON File'
                  }
                  styleType="rounded"
                  size="sm"
                  className="border-chariot-stroke-info text-chariot-stroke-info hover:bg-chariot-stroke-info ml-auto"
                  onClick={() => {
                    if (data) {
                      downloadFile({
                        email,
                        name: data.dns,
                        downloadName: data.dns.replace('home/', ''),
                      });
                      toast.dismiss();
                    }
                  }}
                />
              </div>
            );
          },
        });
      }

      return await promise;
    },
  });
}

function getExportLabels(
  type: 'seed' | 'asset' | 'risk' | 'technology' | 'agent'
) {
  switch (type) {
    case 'risk':
      return 'vulnerabilities';
    case 'asset':
      return 'assets';
    case 'seed':
      return 'seeds';
    case 'technology':
      return 'technologies';
    case 'agent':
      return 'agents';
  }
}
